import {
  CourseProgressList,
  EnrolledTraining,
  Interest,
  User,
  UserAccess,
  UserGeneralStatistics,
  UserRanking,
  UserTeam
} from "../../../../domain/entities/entities";
import { userProfileUseCases } from "../../../../domain/usecase/impl/userProfileUseCases";
import { ActionContext } from "vuex";
import { EventCourse } from "../../../../domain/entities/courseEvent/courseEvent";
import { disableBodyOverflow } from "../../../presentation/components/mixins";
import { TrainingCourseRequest } from "../../../../domain/entities/courseEvent/training_course";
import { Order } from "domain/entities/order";
import { ListResponse } from "domain/entities/shop/product/listResponse";
import { RootState } from "../store";

class UserProfileState {
  user: User | null = null;
  userInterests: Interest[] = [];
  courseProgressList: CourseProgressList = new CourseProgressList();
  enrolledTraining: EnrolledTraining[] = [];
  sortCourseByProgress: string = "";
  redoOnBoarding: boolean = false;
  userEvents: EventCourse[] = [];
  userActiveEvent: EventCourse = new EventCourse();
  userActiveTraining: TrainingCourseRequest = new TrainingCourseRequest();
  teams: UserTeam[] = [];
  usersRanking: UserRanking[] = [];
  generalStatistics: UserGeneralStatistics | null = null;
  accessesStatistics: UserAccess[] = [];
  certificateTraining: string = "";
  public users: User[] = [];
  userPages = 1;
}

const state = new UserProfileState();

const mutations = {
  SET_USER(state: UserProfileState, user: User | null): void {
    state.user = user;
  },

  SET_USER_INTERESTS(
    state: UserProfileState,
    interests: Array<Interest>
  ): void {
    state.userInterests = interests;
  },
  LOAD_USER_COURSES_IN_PROGRESS(
    state,
    payload: { list: CourseProgressList; index: number }
  ) {
    const { list, index } = payload;

    if (index === 0) {
      state.courseProgressList = list;
    } else {
      state.courseProgressList.courses.push(...list.courses);
      state.courseProgressList.hasMore = list.hasMore;
    }
  },
  ENROLLED_TRAININGS(
    state: UserProfileState,
    enrolledTraining: EnrolledTraining[]
  ): void {
    state.enrolledTraining = enrolledTraining;
  },

  LOAD_USER_EVENTS(state: UserProfileState, userEvents: EventCourse[]): void {
    state.userEvents = userEvents;
  },

  LOAD_USER_ACTIVE_EVENT(
    state: UserProfileState,
    userActiveEvent: EventCourse
  ): void {
    state.userActiveEvent = userActiveEvent;
  },

  LOAD_USER_ACTIVE_TRAINING(
    state: UserProfileState,
    userActiveTraining: TrainingCourseRequest
  ): void {
    state.userActiveTraining = userActiveTraining;
  },

  SORT_COURSES_BY_PROGRESS(state: UserProfileState, value: string): void {
    state.sortCourseByProgress = value;
  },

  REDO_ON_BOARDING(sate: UserProfileState, redoOnBoarding: boolean): void {
    state.redoOnBoarding = redoOnBoarding;
  },

  SET_TEAMS(state: UserProfileState, teams: UserTeam[]) {
    state.teams = teams;
  },

  SET_USERS_RANKING(state: UserProfileState, usersRanking: UserRanking[]) {
    state.usersRanking = usersRanking;
  },

  SET_GENERAL_STATISTICS(
    state: UserProfileState,
    generalStatistics: UserGeneralStatistics
  ) {
    state.generalStatistics = generalStatistics;
  },

  SET_ACCESSES_STATISTICS(
    state: UserProfileState,
    accessesStatistics: UserAccess[]
  ) {
    state.accessesStatistics = accessesStatistics;
  },

  LOAD_USER_CERTIFICATE(state: UserProfileState, certificate: string) {
    state.certificateTraining = certificate;
  },
  SET_USERS(state: UserProfileState, users: User[]): void {
    state.users = users;
  },
  SET_PAGES(state: UserProfileState, userPages: number): void {
    state.userPages = userPages;
  }
};

const actions = {
  async loadUser({
    commit
  }: ActionContext<UserProfileState, UserProfileState>): Promise<void> {
    const user = await userProfileUseCases.get();
    commit("SET_USER", user);
  },

  async loadTeams({
    commit
  }: ActionContext<UserProfileState, UserProfileState>): Promise<void> {
    const teams: UserTeam[] = await userProfileUseCases.getTeams();
    commit("SET_TEAMS", teams);
  },

  async loadUsersRanking({
    commit
  }: ActionContext<UserProfileState, UserProfileState>): Promise<void> {
    const usersRanking: UserRanking[] = await userProfileUseCases.getUsersRanking();
    commit("SET_USERS_RANKING", usersRanking);
  },

  async loadGeneralStatistics({
    commit
  }: ActionContext<UserProfileState, UserProfileState>): Promise<void> {
    const generalStatistics: UserGeneralStatistics = await userProfileUseCases.getGeneralStatistics();
    commit("SET_GENERAL_STATISTICS", generalStatistics);
  },

  async loadUserAccesses({
    commit
  }: ActionContext<UserProfileState, UserProfileState>): Promise<void> {
    const generalStatistics: UserAccess[] = await userProfileUseCases.getAccessesStatistics();
    commit("SET_ACCESSES_STATISTICS", generalStatistics);
  },

  async validateUserIsLogged({
    commit
  }: ActionContext<UserProfileState, UserProfileState>): Promise<void> {
    return await userProfileUseCases.validateUserIsLogged();
  },
  async editUser(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    user: User
  ): Promise<void> {
    await userProfileUseCases.edit(user);
    commit("SET_USER", user);
  },

  async editUserInterests(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    interests: Array<Interest>
  ): Promise<void> {
    await userProfileUseCases.editUserInterests(interests);
    commit("SET_USER_INTERESTS", interests);
  },

  updateUser(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    user: User | null
  ): void {
    commit("SET_USER", user);
  },

  async loadUserCoursesInProgress({ commit }, { index }): Promise<void> {
    try {
      const list: CourseProgressList = await userProfileUseCases.getUserCoursesInProgress(
        index
      );

      commit("LOAD_USER_COURSES_IN_PROGRESS", { list, index });
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async listEnrolledTraining(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    order: Order
  ): Promise<void> {
    const response: EnrolledTraining[] = await userProfileUseCases.getUserEnrolledTraining(
      order
    );
    commit("ENROLLED_TRAININGS", response);
  },

  async loadUserEvents({
    commit
  }: ActionContext<UserProfileState, UserProfileState>): Promise<void> {
    const eventCourses: EventCourse[] = await userProfileUseCases.getUserEvents();
    commit("LOAD_USER_EVENTS", eventCourses);
  },

  async loadUserActiveEvent(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    eventId: number
  ): Promise<void> {
    commit(
      "LOAD_USER_ACTIVE_EVENT",
      await userProfileUseCases.getUserEvent(eventId)
    );
  },

  async loadUserActiveTraining(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    eventId: number
  ): Promise<void> {
    commit(
      "LOAD_USER_ACTIVE_TRAINING",
      await userProfileUseCases.getUserTraining(eventId)
    );
  },

  sortUserCoursesByProgress(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    value: string
  ): void {
    commit("SORT_COURSES_BY_PROGRESS", value);
  },

  redoOnBoarding(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    redoOnBoarding: boolean
  ): void {
    commit("REDO_ON_BOARDING", redoOnBoarding);
    disableBodyOverflow();
  },

  async getCertificate(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    trainingID: number
  ): Promise<void> {
    commit(
      "LOAD_USER_CERTIFICATE",
      await userProfileUseCases.getUserCertificate(trainingID)
    );
  },

  async listUsers(
    { commit }: ActionContext<UserProfileState, UserProfileState>,
    order: Order
  ): Promise<void> {
    try {
      const listResponse: ListResponse<User> = await userProfileUseCases.listUsers(
        order
      );
      commit("SET_USERS", listResponse.items);
      commit("SET_PAGES", listResponse.page);
    } catch (e) {
      console.error(e);
    }
  },

  async indicationTrainingUser(
    _: ActionContext<UserProfileState, UserProfileState>,
    payload: { userIDs: number[]; courseID: number }
  ): Promise<void> {
    await userProfileUseCases.indicationTrainingUser(
      payload.userIDs,
      payload.courseID
    );
  }
};

const getters = {
  user: (state: UserProfileState): User | null => {
    const localUser = localStorage.getItem("user");
    let user = null;
    if (localUser) {
      user = JSON.parse(localUser);
    }
    return state.user || user;
  },
  userInterests: (state: UserProfileState) => state.userInterests || [],
  courseProgressList: (state: UserProfileState): CourseProgressList =>
    state.courseProgressList,
  enrolledTraining: (state: UserProfileState): EnrolledTraining[] =>
    state.enrolledTraining,
  userEvents: (state: UserProfileState): EventCourse[] =>
    state.userEvents || [],
  userActiveEvent: (state: UserProfileState): EventCourse =>
    state.userActiveEvent,
  userActiveTraining: (state: UserProfileState): TrainingCourseRequest =>
    state.userActiveTraining,
  sortCourseByProgress: (state: UserProfileState) => state.sortCourseByProgress,
  redoOnBoarding: (state: UserProfileState) => state.redoOnBoarding,
  teams: (state: UserProfileState) => state.teams,
  usersRanking: (state: UserProfileState) => state.usersRanking,
  generalStatistics: (state: UserProfileState) => state.generalStatistics,
  userAccesses: (state: UserProfileState) => state.accessesStatistics,
  certificateTraining: (state: UserProfileState) => state.certificateTraining,
  users: (state: UserProfileState): User[] => state.users,
  userPages: (state: UserProfileState): number => state.userPages
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
